import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import { ajustarTamanhoFonte, coresTextosGlobais, tamanhosControlesGlobais, tamanhosPaginasAdministrativas } from "../../estilos/global";

const formatarData = (valor, somenteData = false) => {
  const data = valor ? new Date(valor) : null;
  return data && !Number.isNaN(data.getTime())
    ? data.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", ...(!somenteData && { timeStyle: "short" }) })
    : "Data não informada";
};

const chaveRegistro = (registro) => registro.chave_registro || `${registro.tipo || "devolucao"}:${registro.id}`;

// Cada devolução inicia um ciclo; os reenvios seguintes pertencem a ela até a próxima devolução.
// Reenvios sem uma devolução anterior continuam visíveis, sem inventar uma correção.
const agruparCiclos = (registros) => {
  const cronologicos = [...registros].reverse().sort((a, b) => {
    const diferenca = new Date(a.data_hora).getTime() - new Date(b.data_hora).getTime();
    if (!Number.isFinite(diferenca)) return 0;
    return diferenca || Number(a.tipo === "reenvio") - Number(b.tipo === "reenvio");
  });
  const ciclos = [];
  let numero = 0;
  cronologicos.forEach((registro) => {
    if (registro.tipo !== "reenvio") {
      numero += 1;
      ciclos.push({ chave: chaveRegistro(registro), numero, data: registro.data_hora, registros: [registro] });
    } else if (ciclos.length) {
      ciclos[ciclos.length - 1].registros.push(registro);
    } else {
      ciclos.push({ chave: `sem-devolucao:${chaveRegistro(registro)}`, numero: null, data: registro.data_hora, registros: [registro] });
    }
  });
  return ciclos.reverse();
};

export default function DevolutivasDocumentoDialog({ open, documento, registros, carregando, erro, erroAnexo, onClose, onRetry, onBaixarAnexo, titulo = "Histórico do documento" }) {
  const [abertas, setAbertas] = useState(() => new Set());
  const ciclos = useMemo(() => agruparCiclos(registros), [registros]);

  useEffect(() => {
    setAbertas(new Set(open && ciclos.length ? [ciclos[0].chave] : []));
  }, [open, ciclos]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="devolutivas-titulo"
      aria-describedby="devolutivas-documento"
      PaperProps={{ sx: { width: 720, maxWidth: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" }, height: "min(560px, calc(100dvh - 48px))", maxHeight: "calc(100dvh - 48px)", m: { xs: 1.5, sm: 3 }, borderRadius: "10px", borderTop: "4px solid var(--sear-borda-forte, #087cf0)", overflow: "hidden" } }}
    >
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.5, flexShrink: 0, borderBottom: "1px solid var(--sear-borda, #dfebf8)" }}>
        <DialogTitle id="devolutivas-titulo" sx={{ p: 0, mb: 1, color: coresTextosGlobais.tituloPagina, fontSize: ajustarTamanhoFonte("18px"), fontWeight: 600, lineHeight: 1.25 }}>
          {titulo}
        </DialogTitle>
        <Typography sx={{ color: "var(--sear-texto-secundario, #7185a0)", fontSize: tamanhosControlesGlobais.rotulo }}>
          Correções e reenvios organizados por ciclo
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 0.75 }}>
          <Typography id="devolutivas-documento" sx={{ color: coresTextosGlobais.conteudoTabela, fontSize: tamanhosPaginasAdministrativas.conteudoTabela, overflowWrap: "anywhere" }}>
            {documento?.nome_documento}
          </Typography>
          {!carregando && !erro && registros.length > 0 && (
            <Chip label={registros.length + (registros.length === 1 ? " registro" : " registros")} size="small" sx={{ bgcolor: "var(--sear-superficie-azul, #eaf4ff)", color: "var(--sear-azul, #0875df)", height: 22, fontSize: tamanhosControlesGlobais.rotulo }} />
          )}
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 1, minHeight: 0, flex: "1 1 auto", overflowY: "auto", overscrollBehavior: "contain", scrollbarGutter: "stable" }}>
        {carregando ? (
          <Box role="status" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4, color: coresTextosGlobais.conteudoTabela }}>
            <CircularProgress size={24} aria-hidden="true" />
            <Typography sx={{ fontSize: tamanhosControlesGlobais.descricao }}>Carregando histórico...</Typography>
          </Box>
        ) : erro ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Tentar novamente</Button>}>{erro}</Alert>
        ) : registros.length === 0 ? (
          <Alert severity="info">Nenhuma movimentação registrada para este documento.</Alert>
        ) : (
          <Box component="ol" aria-label="Ciclos de correção do documento" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {ciclos.map((ciclo) => (
              <Box component="li" key={ciclo.chave} sx={{ borderBottom: "1px solid var(--sear-borda, #e2ebf5)", "&:last-child": { borderBottom: 0 } }}>
                <Box
                  component="details"
                  aria-label={ciclo.numero ? `${ciclo.numero}ª correção` : "Reenvios anteriores"}
                  open={abertas.has(ciclo.chave)}
                  onToggle={(event) => {
                    const expandida = event.currentTarget.open;
                    setAbertas((anteriores) => {
                      if (anteriores.has(ciclo.chave) === expandida) return anteriores;
                      const proximas = new Set(anteriores);
                      if (expandida) proximas.add(ciclo.chave); else proximas.delete(ciclo.chave);
                      return proximas;
                    });
                  }}
                  sx={{ color: coresTextosGlobais.conteudoTabela, "&[open] .devolutiva-seta": { transform: "rotate(180deg)" } }}
                >
                  <Box component="summary" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, py: 1.5, cursor: "pointer", listStyle: "none", "&::-webkit-details-marker": { display: "none" }, "&:focus-visible": { outline: "2px solid var(--sear-borda-forte, #087cf0)", outlineOffset: 2 } }}>
                    <ExpandMoreRoundedIcon className="devolutiva-seta" aria-hidden="true" sx={{ color: "var(--sear-texto-suave, #779ac0)", fontSize: 18 }} />
                    <Typography component="span" sx={{ fontSize: tamanhosControlesGlobais.descricao, fontWeight: 600 }}>
                      {ciclo.numero ? `${ciclo.numero}ª correção` : "Reenvios anteriores"}
                    </Typography>
                    <Typography component="time" dateTime={ciclo.data || undefined} sx={{ fontSize: tamanhosControlesGlobais.rotulo, color: "var(--sear-texto-secundario, #7185a0)" }}>{formatarData(ciclo.data, true)}</Typography>
                  </Box>
                  <Box component="ol" aria-label={ciclo.numero ? `Movimentações da ${ciclo.numero}ª correção` : "Reenvios sem devolução registrada"} sx={{ listStyle: "none", m: 0, p: 0, pb: 0.5 }}>
                    {ciclo.registros.map((registro) => {
                      const reenvio = registro.tipo === "reenvio";
                      const cor = reenvio ? "var(--sear-sucesso-texto, #128465)" : "var(--sear-azul, #0875df)";
                      return (
                    <Box component="li" key={chaveRegistro(registro)} sx={{ mb: 1.25, px: 1.5, py: 1.25, borderLeft: `3px solid ${cor}`, bgcolor: reenvio ? "var(--sear-sucesso-fundo, #eef9f4)" : "var(--sear-superficie-azul, #edf5ff)" }}>
                      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: cor }}>
                          {reenvio ? <FileUploadOutlinedIcon sx={{ fontSize: 16 }} /> : <AssignmentReturnOutlinedIcon sx={{ fontSize: 16 }} />}
                          <Typography component="span" sx={{ fontSize: tamanhosControlesGlobais.rotulo, fontWeight: 600 }}>{reenvio ? "Escola · Documento reenviado" : "Analista · Correção solicitada"}</Typography>
                        </Box>
                        <Typography component="time" dateTime={registro.data_hora || undefined} sx={{ ml: { sm: "auto" }, fontSize: tamanhosControlesGlobais.rotulo, color: "var(--sear-texto-secundario, #7185a0)", whiteSpace: "nowrap" }}>{formatarData(registro.data_hora)}</Typography>
                      </Box>
                      {!reenvio && <Typography sx={{ mt: 0.5, color: "var(--sear-texto-secundario, #7185a0)", fontSize: tamanhosControlesGlobais.rotulo, overflowWrap: "anywhere" }}>{registro.responsavel_nome || registro.analista_nome || "Analista não identificado"}</Typography>}
                      <Typography sx={{ mt: 0.75, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontWeight: 400, lineHeight: 1.55, fontSize: tamanhosControlesGlobais.descricao, color: coresTextosGlobais.conteudoTabela }}>{reenvio ? "Documento reenviado para análise." : registro.motivo}</Typography>
                    {reenvio && registro.arquivo_nome && (
                      <Typography sx={{ mt: 0.75, color: "var(--sear-texto-secundario, #7185a0)", fontSize: tamanhosControlesGlobais.rotulo, overflowWrap: "anywhere" }}>
                        Arquivo enviado: {registro.arquivo_nome}
                      </Typography>
                    )}
                    {!reenvio && registro.possui_anexo && (
                      <Button variant="outlined" size="small" startIcon={<DownloadOutlinedIcon />} onClick={() => onBaixarAnexo(registro)} sx={{ mt: 1.25, textTransform: "none", borderRadius: "6px", borderColor: "var(--sear-borda, #daeafa)", bgcolor: "var(--sear-superficie-azul, #edf5ff)", fontSize: tamanhosControlesGlobais.botao, overflowWrap: "anywhere", maxWidth: "100%" }}>
                        {registro.anexo_nome || "Baixar anexo"}
                      </Button>
                    )}
                    </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {!carregando && (erro || registros.length === 0) && documento?.observacao && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "var(--sear-superficie-azul, #f8fbff)", border: "1px solid var(--sear-borda, #dfebfa)", borderRadius: "8px" }}>
            <Typography sx={{ color: coresTextosGlobais.tituloSecao, fontSize: tamanhosControlesGlobais.descricao, mb: 1 }}>Observação atual do documento</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: coresTextosGlobais.conteudoTabela, fontSize: tamanhosControlesGlobais.descricao }}>{documento.observacao}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, py: 1.25, borderTop: "1px solid var(--sear-borda, #dfebf8)", flexShrink: 0, flexWrap: "wrap" }}>
        {erroAnexo && <Alert severity="error" sx={{ width: "100%", mb: 1 }}>{erroAnexo}</Alert>}
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none", borderRadius: "6px", fontSize: tamanhosControlesGlobais.botao, borderColor: "var(--sear-borda, #c7dff8)", ml: "auto" }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
